import normalizeCif from 'utils/normalizeCif';

import { getPartnerIdentifiersByCifs } from 'modules/partners/api/getPartnerIdentifiersByCifs';

import { EncumbranceExcelPartnerRef } from '../types/EncumbrancesExcel';

export const collectCifsFromEncumbranceExcelRawRows = (
  rawRows: Record<string, unknown>[]
): string[] => {
  const cifs: string[] = [];

  rawRows.forEach((row) => {
    const holderCif = normalizeCif(String(row.HOLDER_CIF || ''));
    const partyCif = normalizeCif(String(row.PARTY_CIF || ''));

    if (holderCif) {
      cifs.push(holderCif);
    }
    // Skip placeholder CIFs used for external creditors (AEAT, banks…)
    if (partyCif && partyCif !== 'NA') {
      cifs.push(partyCif);
    }
  });

  return [...new Set(cifs)];
};

/**
 * Resolve partners for Excel CIFs in a single API call.
 * Endpoint: POST /partners/identifiers-by-cif → [{ _id, cif }]
 */
export const fetchExistingPartnersForEncumbrancesExcel = async (
  societyId: string,
  cifs: string[] = []
): Promise<EncumbranceExcelPartnerRef[]> => {
  const uniqueCifs = [
    ...new Set(
      cifs.map((cif) => normalizeCif(cif)).filter((cif) => Boolean(cif))
    ),
  ];

  if (!societyId || !uniqueCifs.length) {
    return [];
  }

  const partners = await getPartnerIdentifiersByCifs({
    societyId,
    cifs: uniqueCifs,
  });

  return partners.reduce<EncumbranceExcelPartnerRef[]>((acc, partner) => {
    if (!partner?._id) {
      return acc;
    }

    const cif = normalizeCif(partner.cif || '');
    if (!cif) {
      return acc;
    }

    acc.push({ _id: String(partner._id), cif });
    return acc;
  }, []);
};

export const buildPartnerByCifMap = (
  partners: EncumbranceExcelPartnerRef[]
): Map<string, EncumbranceExcelPartnerRef> => {
  const map = new Map<string, EncumbranceExcelPartnerRef>();

  partners.forEach((partner) => {
    map.set(partner.cif, partner);
  });

  return map;
};
